// Color configuration for Pre6 website - Updated to match the red logo
// Comprehensive palette based on the red circular logo design

export const colors = {
  // Core brand colors - based on the red logo
  primary: {
    300: '#FC8181', // Light red
    400: '#F56565', // Medium light red
    500: '#E53E3E', // Main logo red
    600: '#C53030', // Deep red
    700: '#9C1C1C', // Darker red
    800: '#742A2A', // Very dark red
  },
  
  // Secondary colors for balance and contrast (light mode)
  secondary: {
    50: '#FFFFFF',   // Pure white
    100: '#F9FAFB',  // Very light gray
    200: '#F3F4F6',  // Light gray
    300: '#E5E7EB',  // Medium light gray
    400: '#D1D5DB',  // Medium gray
    500: '#9CA3AF',  // Medium dark gray
    600: '#6B7280',  // Dark gray
    700: '#4B5563',  // Very dark gray
    800: '#374151',  // Deep gray
    900: '#1F2937',  // Almost black
  },
  
  // Accent colors for highlights and variety
  accent: {
    orange: '#ED8936',  // Warm complement to red
    teal: '#319795',    // Cool modern accent
    purple: '#805AD5',  // Innovation/tech accent
    blue: '#3182CE',    // Professional blue
    green: '#38A169',   // Success/positive actions
  },
  
  // Component-specific colors
  components: {
    button: {
      primary: 'bg-gradient-to-r from-red-500 via-red-600 to-red-700',
      primaryHover: 'hover:from-red-600 hover:via-red-700 hover:to-red-800',
      shadow: 'shadow-red-600/30',
      secondary: 'bg-gradient-to-r from-gray-600 to-gray-700',
      secondaryHover: 'hover:from-gray-700 hover:to-gray-800',
    },
    
    badge: {
      background: 'bg-gray-100',
      border: 'border-gray-200',
      text: 'text-gray-900',
      accent: 'bg-red-50 border-red-200 text-red-700',
    },
    
    card: {
      background: 'bg-gradient-to-br from-white/90 to-gray-50/90',
      border: 'border-gray-200',
      backdrop: 'backdrop-blur-xl',
      accent: 'bg-gradient-to-br from-red-50/80 to-white/90',
    },
    
    svg: {
      primary: 'text-red-600',
      secondary: 'text-red-500',
      indicator: 'bg-red-100 border-red-300 text-red-700',
      accent: 'text-orange-600',
    },
    
    header: {
      background: 'bg-white/80 backdrop-blur-xl',
      border: 'border-gray-200',
      accent: 'bg-red-50/50 backdrop-blur-xl border-red-200',
    },
    
    footer: {
      background: 'bg-gray-50/80 backdrop-blur-xl',
      border: 'border-gray-200',
      text: 'text-gray-600',
      textHover: 'hover:text-gray-900',
      accent: 'bg-red-50/60 backdrop-blur-xl border-red-100',
    },
    
    // Enhanced accent components for special sections
    accent: {
      background: 'bg-gradient-to-r from-red-50 to-red-100',
      border: 'border-red-200',
      text: 'text-red-700',
      cardOverlay: 'bg-gradient-to-br from-red-100 to-transparent',
      hover: 'hover:shadow-red-200',
      secondary: 'bg-gradient-to-r from-orange-50 to-orange-100',
      tertiary: 'bg-gradient-to-r from-teal-50 to-teal-100',
    },
    
    // Status colors
    status: {
      success: 'text-green-700 bg-green-50 border-green-200',
      warning: 'text-orange-700 bg-orange-50 border-orange-200',
      error: 'text-red-700 bg-red-50 border-red-200',
      info: 'text-blue-700 bg-blue-50 border-blue-200',
    },
  },
  
  // Gradient definitions - updated for bright mode
  gradients: {
    text: 'from-red-600 to-red-700',
    textSecondary: 'from-red-500 to-red-600',
    textAccent: 'from-orange-600 to-red-600',
    background: 'from-red-50 to-red-100',
    backgroundSecondary: 'from-orange-50 to-red-50',
    backgroundTertiary: 'from-red-100 to-purple-100',
    card: 'from-red-50/50 to-red-100/50',
    hero: 'from-red-100/60 via-red-50/40 to-transparent',
  },
  
  // Theme variants for different sections (bright mode)
  themes: {
    hero: {
      primary: 'text-red-700',
      secondary: 'text-red-600',
      accent: 'text-orange-700',
    },
    tech: {
      primary: 'text-teal-700',
      secondary: 'text-teal-600',
      accent: 'text-purple-700',
    },
    professional: {
      primary: 'text-blue-700',
      secondary: 'text-blue-600',
      accent: 'text-red-700',
    },
  },
} as const; 